import { https } from "./https";
import {
  BookerSubscriptionPlan,
  OrderItemPlanningTab,
  OrderItemStatus,
  OrderItemUnifiedStatus,
  OrderStatus,
  PaymentStatus,
  TpaAdditionalServiceApprovalStatus,
  TpaApprovalStatus,
} from "./order.enums";

export {
  BookerSubscriptionPlan,
  OrderItemPlanningTab,
  OrderItemStatus,
  OrderItemUnifiedStatus,
  OrderStatus,
  PaymentStatus,
  TpaAdditionalServiceApprovalStatus,
  TpaApprovalStatus,
} from "./order.enums";

export type TimeSlotsParams = {
  partnerProductLocationId: number | string;
  partnerProductId: number | string;
  bookingDate: string; // YYYY-MM-DD
};

export type TimeSlotsResponse = {
  date: string; // YYYY-MM-DD
  slots: string[]; // e.g. "10:15"
};

export async function timeSlots(
  params: TimeSlotsParams,
): Promise<TimeSlotsResponse> {
  const { data } = await https({
    method: "get",
    url: "/order/TimeSlots",
    params,
  });

  return data.data;
}
export type PaymentOrderItemPayload = {
  serviceDate: string;
  serviceTime: string;
  serviceDateTime?: string;
  visitorFirstName: string;
  visitorLastName: string;
  visitorEmailAddress: string;
  visitorPhoneNumber: string;
  hasInsuranceProvider: 0 | 1;
  insuranceProviderName: string;
  insurancePolicyNumber: string;
  partnerProductId: string | number;
  partnerLocationId: string | number;
};

export type PaymentOrderPaymentPayload = {
  provider: "STRIPE" | (string & {});
};

export type PlaceOrderResponseOrderItem = {
  id: number;
  code: string;
  serviceDateTime: string;
  serviceDate: string | null;
  serviceTime: string | null;
  serviceDuration: number;
  productName: string;
  productQuantity: number;
  productPrice: number;
  platformFee: number;
  serviceTaxAmount: number;
  platformFeeTaxAmount: number;
  payableAmount: number;
  paidAmount: number | null;
  discountAmount: number;
  visitorFirstName: string;
  visitorLastName: string;
  visitorPhoneNumber: string;
  visitorEmailAddress: string;
  hasInsuranceProvider: 0 | 1;
  insuranceProviderName: string;
  insurancePolicyNumber: string;
  status: string;
  orderId: number;
  productId: number;
  partnerLocationId: number;
  isReminderSent: boolean;
  createdAt: string;
  modifiedAt: string;
  trxFee: number;
  trxFeeTax: number;
  trxFeePercentage: number;
  trxFeeTaxPercentage: number;
  platformFeeTaxPercentage: number;
  serviceFeeTaxPercentage: number;
  orderPaymentId: number;
  isVoucherUsed: boolean;
  // Response includes nested `product`, `partnerLocation`, etc.
  [k: string]: unknown;
};

export type PlaceOrderResponseOrderPayment = {
  id: number;
  transactionDate: string;
  provider: string;
  status: string;
  payableAmount: number;
  paidAmount: number | null;
  trxFeePercentage: number;
  trxFee: number;
  method: string;
  payload: string; // JSON string from Stripe
  orderId: number;
  checkoutSessionId: string;
  paymentIntentId: string | null;
  invoiceURL: string | null;
  createdAt: string;
  modifiedAt: string;
  [k: string]: unknown;
};

export type PlaceOrderPayload = {
  isGuestOrder: 0 | 1;
  /** When 1, send booking confirmation emails to visitor email(s). Booker (TPA) and clinic notifications are unaffected. */
  sendVisitorBookingConfirmationEmail?: 0 | 1;
  billingPersonFirstName?: string;
  billingPersonLastName?: string;
  billingPersonContactNumber?: string;
  billingPersonEmail?: string;
  telehealth?: boolean;
  partnerId?: string | number;
  countryCode?: string | null;
  bookerFirstName: string;
  bookerLastName: string;
  bookerEmail?: string;
  order_items: PaymentOrderItemPayload[];
};

export type PlaceOrderResponseOrder = PlaceOrderPayload & {
  id: number;
  code: string;
  payableAmount: number;
  paidAmount: number;
  discountAmount: number;
  orderStatus: OrderStatus;
  paymentStatus: PaymentStatus;
  createdAt: string;
  modifiedAt: string;
  refunds: unknown[];
  adminReviews: unknown[];
  partner?: Record<string, unknown>;
  customer?: Record<string, unknown>;
  [k: string]: unknown;
};

export type StripeCheckoutSession = {
  id: string;
  object: "checkout.session" | (string & {});
  url: string | null;
  payment_status?: "unpaid" | "paid" | "no_payment_required" | (string & {});
  status?: "open" | "complete" | "expired" | (string & {});
  mode?: "payment" | "setup" | "subscription" | (string & {});
  currency?: string | null;
  amount_subtotal?: number | null;
  amount_total?: number | null;
  customer_email?: string | null;
  client_secret?: string | null;
  success_url: string;
  metadata?: Record<string, unknown> | null;
  [k: string]: unknown;
};

export type PlaceOrderResponse = {
  order: PlaceOrderResponseOrder;
  clientSecret?: string;
  stripeCheckOutUrl?: string;
  booking?: {
    userEmail: string;
    description: string[];
  };
  extra: Record<string, unknown>;
  [k: string]: unknown;
};

export async function placeOrder({
  telehealth = false,
  ...rest
}: PlaceOrderPayload): Promise<PlaceOrderResponse> {
  const baseUrl = window.location.origin.includes("localhost:")
    ? "https://customer.dev.health4travel.com"
    : window.location.origin;
  const { data } = await https({
    method: "post",
    url: telehealth ? "/order/telehealth/create" : "/order",
    data: {
      medium: "WEB",
      portal: "agent",
      isAvailableNowOrder: false,
      cancel_url: baseUrl,
      success_url: `${baseUrl}/?payment=success`,
      order_payments: [{ provider: "STRIPE" }],
      ...rest,
    },
  });

  return data.data;
}

export type PrintSuccessOrderPdfParams = {
  telehealth_order: boolean;
  free: boolean;
};

export type PrintSuccessOrderPdfResponse = {
  download_url: string;
  expires_in_seconds: number;
  s3_key: string;
};

export async function printSuccessOrderPdf(
  id: string | number,
  params: PrintSuccessOrderPdfParams,
): Promise<PrintSuccessOrderPdfResponse> {
  const { data } = await https({
    method: "post",
    url: `/order/${id}/confirmation-pdf`,
    params,
  });
  return data.data as PrintSuccessOrderPdfResponse;
}

export type OrderItemPartnerLocation = {
  id: number;
  name: string;
  code: string;
  timeZone?: string | null;
  city?: { name?: string };
  partner?: { name?: string };
  [k: string]: unknown;
};

export type OrderItemOrder = {
  id: number;
  code: string;
  orderStatus: OrderStatus;
  paymentStatus: PaymentStatus;
  partner?: { name?: string };
  [k: string]: unknown;
};

export type OrderItemOtherService = {
  id: number;
  code: string | null;
  name?: string;
  productPrice?: number;
  orderItemId?: number;
  paymentStatus?: PaymentStatus | null;
  createdAt?: string;
  modifiedAt?: string;
  createdById?: number | null;
  modifiedById?: number | null;
  trxFee?: number | null;
  trxFeeTax?: number | null;
  trxFeePercentage?: number | null;
  trxFeeTaxPercentage?: number | null;
  payableAmount?: number;
  paidAmount?: number | null;
  orderPaymentId?: number | null;
  tpaApprovalStatus?: TpaApprovalStatus;
  tpaApprovalRequestedAt?: string;
  tpaApprovalRequestedById?: number;
  tpaApprovalDecidedAt?: string;
  tpaApprovalDecidedById?: number;
  tpaApprovalReason?: string;
  approvalRequestId?: string;
};

export type OrderItem = {
  id: number;
  code: string;
  serviceDateTime: string;
  productName: string;
  payableAmount: number;
  paidAmount: number | null;
  visitorFirstName: string;
  visitorLastName: string;
  visitorPhoneNumber: string;
  visitorEmailAddress: string;
  insuranceProviderName: string;
  insurancePolicyNumber: string;
  status: OrderItemStatus | (string & {});
  /**
   * Computed scenario status from `GET /order/orderItems`.
   * Do not overwrite `status` (raw DB status).
   */
  unifiedStatus?:
    | OrderItemUnifiedStatus
    | OrderStatus
    | PaymentStatus.paymentPending
    | PaymentStatus.pendingTpaApproval
    | (string & {});
  orderId: number;
  partnerLocationId: number;
  order?: OrderItemOrder;
  partnerLocation?: OrderItemPartnerLocation;
  other_services?: OrderItemOtherService[];
  originalBooking?: {
    serviceDateTime?: string;
    status?: OrderItemStatus;
    [k: string]: unknown;
  } | null;
  product?: Record<string, unknown>;
  [k: string]: unknown;
};

export type OrderItemsParams = {
  tpaView?: boolean;
  page?: number;
  pageSize?: number;
  orderBy?: string;
  planningTab?: OrderItemPlanningTab | string;
  startDate?: string;
  endDate?: string;
  searchString?: string;
  partnerId?: number | string;
  partnerLocationId?: number | string;
};

export type PaginatedOrderItems = {
  pages: number;
  count: number;
  currentPage: number;
  pageSize: number;
  results: OrderItem[];
};

export type OrderItemsResponse = {
  orders: PaginatedOrderItems;
};

export async function orderItems(
  params: OrderItemsParams = {},
): Promise<OrderItemsResponse> {
  const { data } = await https({
    method: "get",
    url: "/order/orderItems",
    params: { tpaView: true, ...params },
  });
  return data.data;
}

export type OrderDetailItem = OrderItem & {
  serviceDuration?: number;
  productPrice?: number;
  platformFee?: number;
  serviceTaxAmount?: number;
  platformFeeTaxAmount?: number;
  discountAmount?: number;
  trxFee?: number;
  trxFeeTax?: number;
  priceInclTax?: number;
  platformFeeInclTax?: number;
  totalPaidServicesAmount?: number;
  order_payment?: Record<string, unknown>;
};

export type AdditionalChargeRequestLineItem = {
  serviceId: number;
  orderItemId?: number;
  visitorName?: string;
  serviceName?: string;
  label: string;
  amount: number;
};

export type AdditionalChargeRequestTotals = {
  productAmount: number;
  trxFee: number;
  trxFeePercentage?: number;
  trxFeeTax: number;
  trxFeeTaxPercentage?: number;
  payableAmount: number;
  paidAmount: number;
  outstandingAmount: number;
};

export type AdditionalChargeRequest = {
  approvalRequestId: string;
  title: string;
  statusText?: string;
  isLatest?: boolean;
  requestedAt?: string;
  requestedById?: number;
  status?: TpaApprovalStatus | PaymentStatus | string;
  showApprovalActions?: boolean;
  isBlocking?: boolean;
  canGeneratePayment?: boolean;
  canCheckout?: boolean;
  serviceIds?: number[];
  orderPaymentId?: number | null;
  lineItems: AdditionalChargeRequestLineItem[];
  totals: AdditionalChargeRequestTotals;
};

export type OrderDetail = {
  id: number;
  code: string;
  payableAmount: number;
  paidAmount: number;
  discountAmount: number;
  status?: PaymentStatus | OrderStatus;
  orderStatus: OrderStatus;
  paymentStatus: PaymentStatus;
  billingPersonFirstName: string;
  billingPersonLastName: string;
  billingPersonContactNumber: string;
  billingPersonEmail: string;
  bookerFirstName: string | null;
  bookerLastName: string | null;
  bookerEmail?: string | null;
  isBookedByTPA?: boolean;
  bookerSubscriptionPlan?: BookerSubscriptionPlan | string | null;
  bookerSubscriptionName?: string | null;
  createdAt: string;
  cardTransactionFee?: number;
  cardTransactionFeeInclusiveTax?: number;
  totalPaidServicesAmount?: number;
  refundedAmount?: number;
  order_items: OrderDetailItem[];
  additionalChargeRequests?: AdditionalChargeRequest[];
  order_payments?: Record<string, unknown>[];
  partner?: { name?: string; [k: string]: unknown };
  customer?: Record<string, unknown>;
  adminReviews?: Record<string, unknown>[];
  [k: string]: unknown;
};

export type OrderDetailResponse = {
  order: OrderDetail;
};

export type GetOrderParams = {
  tpaView?: boolean;
};

export async function getOrder(
  id: string | number,
  params: GetOrderParams = {},
): Promise<OrderDetailResponse> {
  const { data } = await https({
    method: "get",
    url: `/order/${id}`,
    params: { tpaView: true, ...params },
  });
  return data.data;
}

export type TpaAdditionalServicesActionPayload = {
  approvalRequestId?: string;
  reason?: string;
};

export type TpaRejectAdditionalServicesPayload =
  TpaAdditionalServicesActionPayload & {
    reason: string;
  };

export type TpaAdditionalServiceApprovalResponse = {
  status: TpaApprovalStatus;
  notificationsSent: boolean;
};

export async function updateAdditionalServiceApproval(
  id: string | number,
  status: TpaAdditionalServiceApprovalStatus,
  payload?: TpaAdditionalServicesActionPayload,
): Promise<TpaAdditionalServiceApprovalResponse> {
  const { data } = await https({
    method: "post",
    url: `/order/${id}/additional-services/${status}`,
    data: payload,
  });
  return data.data;
}
